import { FC, UIEventHandler, useEffect, useState } from 'react'
import { useFormikContext } from 'formik'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { Configuration, Flux } from '@/api'
import useTranslate from '@/hooks/use-translate'
import DialogUploadValues from '@/types/dialogUploadValues'
import { NfField } from '../primitives/NfField'
import { nfMenuPaperSx } from '../nfFieldSx'
import { useWizardWorkflowOptions } from './useWizardWorkflowOptions'

export interface WorkflowFieldProps {
  configurations: Configuration[]
}

const SCROLL_THRESHOLD_PX = 40

/**
 * Flux de traitement — optionnel. Recherche par nom côté serveur et pagination au défilement
 * (`useWizardWorkflowOptions`) : une agence peut avoir bien plus de modèles qu'une page.
 */
export const WorkflowField: FC<WorkflowFieldProps> = ({ configurations }) => {
  const formik = useFormikContext<DialogUploadValues>()
  const { t: tUpload } = useTranslate('upload')

  const hasGroup = !!formik.values.group

  const selectedConfiguration = configurations.find(
    (c) => c.cid === formik.values.exportConfiguration
  )

  const workflowOptions = useWizardWorkflowOptions({
    groupId: formik.values.group || undefined,
    configuration: selectedConfiguration,
  })
  const { options, search, setSearch, isLoading } = workflowOptions

  // Le modèle choisi reste affiché même quand la recherche en cours ne le renvoie plus.
  const [selectedWorkflow, setSelectedWorkflow] = useState<Flux | null>(null)
  useEffect(() => {
    if (!formik.values.workflow) {
      setSelectedWorkflow(null)
      return
    }
    const loaded = options.find(
      (option) => option.fid === formik.values.workflow
    )
    if (loaded) setSelectedWorkflow(loaded)
  }, [formik.values.workflow, options])

  const selectedValue =
    selectedWorkflow?.fid === formik.values.workflow ? selectedWorkflow : null

  const noWorkflowAvailable =
    hasGroup &&
    search.trim() === '' &&
    !isLoading &&
    !workflowOptions.isFetchingNextPage &&
    !workflowOptions.hasNextPage &&
    options.length === 0

  const handleScroll: UIEventHandler<HTMLElement> = (event) => {
    const listbox = event.currentTarget
    const remaining =
      listbox.scrollHeight - listbox.scrollTop - listbox.clientHeight
    if (
      remaining < SCROLL_THRESHOLD_PX &&
      workflowOptions.hasNextPage &&
      !workflowOptions.isFetchingNextPage
    ) {
      void workflowOptions.fetchNextPage()
    }
  }

  return (
    <NfField
      label={tUpload('wizardWorkflowLabel')}
      tooltip={tUpload('wizardWorkflowTooltip')}
      disabledReason={
        !hasGroup
          ? tUpload('wizardChooseGroupFirst')
          : noWorkflowAvailable
          ? tUpload('wizardNoWorkflowForCombination')
          : null
      }
    >
      <Autocomplete
        key={formik.values.group}
        options={options}
        value={selectedValue}
        disabled={!hasGroup}
        loading={isLoading || workflowOptions.isFetchingNextPage}
        filterOptions={(loadedOptions) => loadedOptions}
        getOptionLabel={(option: Flux) => option?.name || ''}
        isOptionEqualToValue={(option, value) => option?.fid === value?.fid}
        id="workflow"
        onInputChange={(_event, value, reason) => {
          // `reset` : MUI recopie le libellé du modèle choisi, ce n'est pas une recherche.
          setSearch(reason === 'input' ? value : '')
        }}
        onChange={(_event, newValue) => {
          setSelectedWorkflow(newValue)
          formik.setFieldValue('workflow', newValue ? newValue.fid : '')
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={tUpload('wizardWorkflowPlaceholder')}
            variant="outlined"
          />
        )}
        ListboxProps={{
          onScroll: handleScroll,
          sx: { '& .MuiAutocomplete-option': { fontSize: 11.5 } },
        }}
        noOptionsText={tUpload('wizardNoWorkflowFound')}
        loadingText={tUpload('wizardOptionsLoading')}
        slotProps={{ paper: { sx: nfMenuPaperSx } }}
        data-testid="workflow-autocomplete"
      />
    </NfField>
  )
}

export default WorkflowField
